/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import LeafletMap from './LeafletMap/LeafletMap';
import Overlay from './LeafletMap/Overlay';
import Marker from './LeafletMap/Marker';
import ViewState from './LeafletMap/ViewState';
import React, { Component, Children, ComponentProps } from 'react';
import { fitToCover } from '../../support/Utils';
import shallowEqual from '../../support/helpers/shallowEqual';
import autobind from 'class-autobind';
import isObject from '../../support/shared/isObject';

type NamedDimensions = { width: number; height: number };
type Dimensions = [number, number];
type CoordsCallback = (left: number, top: number) => any;

type MapViewMarkerProps = Omit<ComponentProps<typeof Marker>, 'lat' | 'lng'> & {
  itemKey: string;
  left: number;
  top: number;
  onClick?: CoordsCallback;
  onMove?: CoordsCallback;
};

class MapViewMarker extends Component<MapViewMarkerProps> {
  render() {
    return null;
  }
}

type LeafletMapProps = ComponentProps<typeof LeafletMap>;
type LeafletMapRequiredProps = Omit<
  LeafletMapProps,
  'viewState' | 'onViewStateChange' | 'onClick' | 'onZoomReset'
>;

type Props = LeafletMapRequiredProps & {
  children?: React.ReactNode;
  width: number;
  height: number;
  onMapClick?: CoordsCallback;

  // overlay
  imageUrl: string;

  imageWidth: number;
  imageHeight: number;
};

type State = {
  viewState: ViewState;
  overlaySize: Dimensions;
};

// const MAP_OPTIONS = {
//   attributionControl: false,
//   minZoom: 6,
//   maxZoom: 14,
//   doubleClickZoom: false,
//   scrollWheelZoom: false,
// };

const INITIAL_LAT = 0;
const INITIAL_LNG = 0;
const INITIAL_ZOOM_LEVEL = 9;
const OVERLAY_KEY = 'OVERLAY_KEY';
const POINTS_DIVISOR = 10000;

class MapView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    autobind(this);
    let viewState = new ViewState({
      lat: INITIAL_LAT,
      lng: INITIAL_LNG,
      zoomLevel: INITIAL_ZOOM_LEVEL,
    });
    this.state = {
      viewState: viewState,
      overlaySize: this._toUnits(viewState, this._getOverlaySize(this.props)),
    };
  }

  componentDidUpdate(oldProps: Props) {
    let newProps = this.props;
    let didMapSizeChange =
      oldProps.width !== newProps.width || oldProps.height !== newProps.height;
    let didImageSizeChange =
      oldProps.imageWidth !== newProps.imageWidth ||
      oldProps.imageHeight !== newProps.imageHeight;
    if (didMapSizeChange || didImageSizeChange) {
      let viewState = this.state.viewState;
      this.setState({
        overlaySize: this._toUnits(viewState, this._getOverlaySize(newProps)),
      });
    }
  }

  shouldComponentUpdate(newProps: Props, newState: State) {
    return (
      !shallowEqual(this.props, newProps) || !shallowEqual(this.state, newState)
    );
  }

  render() {
    let {
      onMapClick,
      imageUrl,
      imageWidth,
      imageHeight,
      ...otherProps
    } = this.props;
    let { overlaySize, viewState } = this.state;
    let [width, height] = overlaySize;
    return (
      <LeafletMap
        {...otherProps}
        viewState={viewState}
        onViewStateChange={this._onViewStateChange}
        onClick={this._getCoordTranslator(onMapClick)}
        onZoomReset={this._resetZoom}
      >
        <Overlay
          itemKey={OVERLAY_KEY}
          url={imageUrl}
          width={width}
          height={height}
        />
        {this._renderMarkers()}
      </LeafletMap>
    );
  }

  _renderMarkers(): Array<React.ReactNode> {
    let isMarker = (
      el: unknown,
    ): el is React.ReactComponentElement<typeof MapViewMarker> => {
      return isObject(el) && el.type === MapViewMarker;
    };
    return (
      Children.map(this.props.children, (child: any) => {
        if (child == null || typeof child !== 'object') {
          return null;
        }
        let el = isMarker(child) ? child : null;
        if (!el) {
          throw new Error('MapView children must be of type MapViewMarker');
        }
        let { left, top, itemKey, onMove, ...others } = el.props;
        let { lat, lng } = this._toLatLng(+left, +top);
        return (
          <Marker
            {...others}
            itemKey={String(itemKey)}
            lat={+lat}
            lng={+lng}
            onMove={this._getCoordTranslator(onMove as any)}
          />
        );
      }) ?? []
    );
  }

  _onViewStateChange(viewState: ViewState) {
    let [width, height] = this.state.overlaySize;
    let { lat, lng, zoomLevel } = viewState;
    let latLimit = height / 2;
    let lngLimit = width / 2;
    if (lat > latLimit) {
      lat = latLimit;
    }
    if (lat < -latLimit) {
      lat = -latLimit;
    }
    if (lng > lngLimit) {
      lng = lngLimit;
    }
    if (lng < -lngLimit) {
      lng = -lngLimit;
    }
    if (lat !== viewState.lat || lng !== viewState.lng) {
      viewState = new ViewState({ lat, lng, zoomLevel });
    }
    this.setState({ viewState });
  }

  _resetZoom() {
    this.setState({
      viewState: new ViewState({
        lat: INITIAL_LAT,
        lng: INITIAL_LNG,
        zoomLevel: INITIAL_ZOOM_LEVEL,
      }),
    });
  }

  _getOverlaySize(props: Props): Dimensions {
    let dimensions: NamedDimensions = fitToCover(
      props.imageWidth,
      props.imageHeight,
      props.width,
      props.height,
    );
    let { width, height } = dimensions;
    return [width, height];
  }

  _getCoordTranslator(callback: CoordsCallback | null | undefined) {
    if (!callback) {
      return null;
    }
    return (lat: number, lng: number) => {
      let { left, top } = this._toLeftTop(lat, lng);
      callback && callback(left, top);
    };
  }

  _toLatLng(left: number, top: number) {
    let [width, height] = this.state.overlaySize;
    return {
      lat: (top / POINTS_DIVISOR) * height - height / 2,
      lng: (left / POINTS_DIVISOR) * width - width / 2,
    };
  }

  _toUnits(viewState: ViewState, coords: Dimensions): Dimensions {
    let pixelsPerUnit = viewState.getPixelsPerUnit(INITIAL_ZOOM_LEVEL);
    let [x, y] = coords;
    return [x / pixelsPerUnit, y / pixelsPerUnit];
  }

  _toLeftTop(lat: number, lng: number) {
    let [width, height] = this.state.overlaySize;
    return {
      top: ((height / 2 + lat) * POINTS_DIVISOR) / height,
      left: ((width / 2 + lng) * POINTS_DIVISOR) / width,
    };
  }
}

export { MapViewMarker as Marker };
export default MapView;
